import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Target, UploadSimple, TrendDown, TrendUp, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const Metric = ({ label, value, suffix = "", good, hint }) => (
  <div className="border border-zinc-800 px-2.5 py-2" title={hint}>
    <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`mono text-lg font-black leading-tight ${good === true ? "text-[#39FF14]" : good === false ? "text-[#FF3B30]" : "text-white"}`}>
      {value == null ? "—" : `${value}${suffix}`}
    </div>
  </div>
);

export default function OwnershipCalibrationPanel() {
  const [text, setText] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState(null);
  const fileRef = useRef(null);

  const loadHistory = useCallback(() => {
    api.ownCalHistory().then(setHist).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const readFile = (f) => {
    if (!f) return;
    if (!label) setLabel(f.name.replace(/\.csv$/i, ""));
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ""));
    r.onerror = () => toast.error("Could not read that file");
    r.readAsText(f);
  };

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.ownCalUpload(text, label || "DK contest");
      setResult(r.graded);
      setText("");
      loadHistory();
      toast.success(`Graded ${r.graded.metrics.n_matched} players`, {
        description: r.graded.prelock
          ? "Scored against the pre-lock snapshot."
          : "No pre-lock snapshot existed — scored against the current pool.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grading failed");
    } finally {
      setBusy(false);
    }
  };

  const m = result?.metrics;

  return (
    <section className="bg-[#0a0a0a] border border-zinc-800 p-4 space-y-3" data-testid="own-cal-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-2">
            <Target size={13} weight="fill" className="text-[#FFCC00]" /> Ownership calibration
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 max-w-xl leading-relaxed">
            Drop in a DK <span className="mono text-zinc-300">contest-standings-XXXXXX.csv</span> after the slate.
            We read its <span className="mono text-zinc-300">%Drafted</span> column and score the ownership we
            predicted before lock — the number the whole anti-field thesis rests on.
          </p>
        </div>
        {hist && (
          <div className="flex items-center gap-2">
            <Metric label="Avg MAE" value={hist.avg_mae} suffix="%" hint="Average absolute ownership error across graded contests" />
            <Metric label="Best" value={hist.best_mae} suffix="%" />
            {hist.mae_trend != null && (
              <div className="border border-zinc-800 px-2.5 py-2" data-testid="own-cal-trend">
                <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Trend</div>
                <div className={`mono text-lg font-black leading-tight inline-flex items-center gap-1 ${hist.mae_trend <= 0 ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                  {hist.mae_trend <= 0 ? <TrendDown size={14} weight="bold" /> : <TrendUp size={14} weight="bold" />}
                  {Math.abs(hist.mae_trend)}%
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Contest label (optional)"
          data-testid="own-cal-label"
          className="bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-1.5 mono text-[11px] text-zinc-200 w-56" />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="own-cal-pick"
          className="mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#FFCC00] inline-flex items-center gap-1">
          <UploadSimple size={12} /> Choose standings CSV
        </button>
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden"
          onChange={(e) => readFile(e.target.files?.[0])} data-testid="own-cal-file" />
        {text && <span className="mono text-[10px] text-[#39FF14]" data-testid="own-cal-loaded">{Math.round(text.length / 1024)} KB loaded</span>}
        <button type="button" onClick={submit} disabled={busy || !text.trim()} data-testid="own-cal-grade"
          className="ml-auto mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#FFCC00] text-[#FFCC00] font-bold hover:bg-[#FFCC00] hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#FFCC00] transition-colors">
          {busy ? "Grading…" : "Grade this contest"}
        </button>
      </div>

      {m && (
        <div className="space-y-3" data-testid="own-cal-result">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Metric label="MAE" value={m.mae} suffix="%" hint="Mean absolute ownership error — lower is better" />
            <Metric label="Bias" value={m.bias} suffix="%" hint="Positive = we over-predicted ownership" />
            <Metric label="Chalk hit rate" value={m.chalk_hit_rate} suffix="%" good={m.chalk_hit_rate >= 60}
              hint="How many of the field's real top-10 owned players we had in our top 10" />
            <Metric label="Leverage accuracy" value={m.leverage_accuracy} suffix="%" good={m.leverage_accuracy >= 70}
              hint="Of the players we called under 10% owned, how many actually came in under 10%" />
            <Metric label="Players" value={m.n_matched} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-zinc-800 p-2">
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Error by salary tier</div>
              {Object.entries(m.by_tier || {}).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between mono text-[11px] py-0.5">
                  <span className="text-zinc-400">{k}</span>
                  <span className="text-zinc-600">{v.n} players</span>
                  <span className="text-white font-bold">{v.mae == null ? "—" : `${v.mae}%`}</span>
                  <span className={v.bias > 0 ? "text-[#FF8A00]" : "text-[#4DA6FF]"}>{v.bias == null ? "" : `${v.bias > 0 ? "+" : ""}${v.bias}`}</span>
                </div>
              ))}
            </div>
            <div className="border border-zinc-800 p-2">
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Error by position</div>
              {Object.entries(m.by_position || {}).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between mono text-[11px] py-0.5">
                  <span className="text-zinc-400">{k}</span>
                  <span className="text-zinc-600">{v.n}</span>
                  <span className="text-white font-bold">{v.mae == null ? "—" : `${v.mae}%`}</span>
                  <span className={v.bias > 0 ? "text-[#FF8A00]" : "text-[#4DA6FF]"}>{v.bias == null ? "" : `${v.bias > 0 ? "+" : ""}${v.bias}`}</span>
                </div>
              ))}
            </div>
          </div>

          {(m.worst_misses || []).length > 0 && (
            <div className="border border-zinc-800 p-2" data-testid="own-cal-misses">
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Biggest misses</div>
              {m.worst_misses.slice(0, 6).map((w) => (
                <div key={w.name} className="flex items-baseline justify-between mono text-[11px] py-0.5">
                  <span className="text-zinc-300">{w.name}</span>
                  <span className="text-zinc-600">{w.position} ${w.salary.toLocaleString()}</span>
                  <span className="text-zinc-500">pred {w.pred}%</span>
                  <span className="text-white font-bold">real {w.actual}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {hist?.runs?.length > 0 && (
        <div className="border-t border-zinc-900 pt-2" data-testid="own-cal-history">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Graded contests</div>
          {hist.runs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 mono text-[10px] py-1 border-b border-zinc-900 last:border-0">
              <span className="text-zinc-400 truncate flex-1">{r.contest_label}</span>
              <span className="text-zinc-600">{(r.graded_at || "").slice(0, 10)}</span>
              <span className="text-zinc-500">{r.n_matched}p</span>
              <span className="text-white font-bold">MAE {r.mae}%</span>
              <span className="text-zinc-500">chalk {r.chalk_hit_rate}%</span>
              <button type="button" onClick={async () => { await api.ownCalDelete(r.id); loadHistory(); }}
                data-testid={`own-cal-del-${r.id}`} className="text-zinc-700 hover:text-[#FF3B30]">
                <Trash size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
